import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  ConfirmDialog,
  Alert,
  Paper,
  Text,
  MobileListRow,
  DEFAULT_PAGE_LIMIT,
  formatAddress,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListCustomersQuery,
  useAddUpdateCustomerMutation,
  useDeleteCustomerMutation,
  type Customer,
} from '../api/tenantApi';
import {
  CustomerForm,
  EMPTY_CUSTOMER_FORM,
  customerFormInput,
  type CustomerFormValues,
} from '../features/customers/CustomerForm';
import { useAccess } from '../hooks/useAccess';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

function customerListTitle(customer: Customer): string {
  const addressSnippet = formatAddress(customer.address);
  if (!addressSnippet) return customer.name;
  // Prefer a short snippet (line1) like mobile "(address)"
  const short =
    customer.address?.line1?.trim() ||
    customer.address?.city?.trim() ||
    addressSnippet;
  return `${customer.name} (${short})`;
}

function customerListSubtitle(customer: Customer, noPhone: string): string {
  const place =
    customer.address?.city?.trim() ||
    customer.address?.line2?.trim() ||
    '';
  const phone = customer.phone?.trim() || '';
  if (place && phone) return `${place} - ${phone}`;
  return place || phone || noPhone;
}

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState<CustomerFormValues>(EMPTY_CUSTOMER_FORM);
  const [confirmDelete, setConfirmDelete] = useState<Customer | null>(null);

  const { data, isLoading, isFetching, isError } = useListCustomersQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const [addUpdateCustomer, { isLoading: saving }] = useAddUpdateCustomerMutation();
  const [deleteCustomer, { isLoading: deleting }] = useDeleteCustomerMutation();

  const list = data?.listCustomers;

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_CUSTOMER_FORM);
    setModalOpen(true);
  }

  async function handleSave() {
    await addUpdateCustomer(customerFormInput(form, editing)).unwrap();
    setModalOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    await deleteCustomer(confirmDelete.id).unwrap();
    setConfirmDelete(null);
    setModalOpen(false);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('customers.title')}
        description={t('customers.description')}
      />

      {isError ? (
        <Alert color="red" title={t('customers.loadFailed')}>
          {t('common.connectionError')}
        </Alert>
      ) : null}

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('customers.searchPlaceholder')}
        emptyTitle={t('customers.emptyTitle')}
        emptyDescription={t('customers.emptyDescription')}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
        mobileView="list"
        addButton={
          canMutate() ? <Button onClick={openCreate}>{t('customers.add')}</Button> : undefined
        }
        columns={[
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'phone', header: t('common.phone'), render: (row) => row.phone || t('common.emptyValue') },
          { key: 'email', header: t('common.email'), render: (row) => row.email || t('common.emptyValue') },
          {
            key: 'approval',
            header: t('approval.status'),
            render: (row) => <ApprovalStatusBadge status={row.approvalStatus} />,
          },
          {
            key: 'balance',
            header: t('customers.openingBalance'),
            render: (row) => row.openingBalance ?? 0,
          },
        ]}
        renderCard={(row: Customer) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.name}</Text>
                <ApprovalStatusBadge status={row.approvalStatus} />
              </Group>
              <Text size="sm" c="dimmed">
                {row.phone || t('common.noPhone')} · {row.email || t('common.noEmail')}
              </Text>
              <Text size="sm">{t('common.balance', { amount: row.openingBalance ?? 0 })}</Text>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: Customer) => (
          <MobileListRow
            title={customerListTitle(row)}
            subtitle={customerListSubtitle(row, t('common.noPhone'))}
            onClick={() => navigate(`/customers/${row.id}`)}
          />
        )}
      />

      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('customers.edit') : t('customers.add')}
      >
        <Stack gap="md">
          <CustomerForm value={form} onChange={setForm} />
          <Group justify="flex-end">
            {editing ? (
              <Button color="red" variant="light" onClick={() => setConfirmDelete(editing)}>
                {t('common.delete')}
              </Button>
            ) : null}
            <Button variant="default" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={saving} onClick={() => void handleSave()} disabled={!form.name.trim()}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('customers.deleteConfirm')}
        message={t('common.deleteMessage', { name: confirmDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
